import React, { useMemo, useState } from "react";

import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// Cor da bolinha do evento pelo que vai (ou não vai) acontecer com ele. Segue
// os mesmos significados dos selos da tabela, para não ensinar duas linguagens.
const COR = {
  pending: "bg-primary",
  sent: "bg-green-500",
  cancelled: "bg-muted-foreground",
  failed: "bg-destructive",
};

// Grade mensal dos agendamentos. Clicar num dia abre o modal já com a data;
// clicar num agendamento abre ele para edição.
const ScheduleCalendar = ({ agendamentos = [], onSelecionarDia, onAbrir }) => {
  const [mes, setMes] = useState(() => startOfMonth(new Date()));

  const dias = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(mes)),
        end: endOfWeek(endOfMonth(mes)),
      }),
    [mes]
  );

  // Agrupa por dia uma vez só: varrer a lista inteira dentro de cada célula
  // multiplicaria o trabalho por 42.
  const porDia = useMemo(() => {
    const mapa = new Map();

    agendamentos.forEach((agendamento) => {
      if (!agendamento.scheduledFor) return;

      const data = parseISO(agendamento.scheduledFor);
      const chave = format(data, "yyyy-MM-dd");

      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave).push({ ...agendamento, quando: data });
    });

    mapa.forEach(lista => lista.sort((a, b) => a.quando - b.quando));

    return mapa;
  }, [agendamentos]);

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-3">
        {/* first-letter em vez de capitalize: este põe maiúscula em "de". */}
        <p className="text-sm font-semibold first-letter:uppercase">
          {format(mes, "MMMM 'de' yyyy", { locale: ptBR })}
        </p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Mês anterior"
            onClick={() => setMes(subMonths(mes, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMes(startOfMonth(new Date()))}
          >
            Hoje
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Próximo mês"
            onClick={() => setMes(addMonths(mes, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b text-center text-xs font-medium text-muted-foreground">
        {DIAS.map((dia) => (
          <div key={dia} className="py-2">
            {dia}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {dias.map((dia) => {
          const doMes = isSameMonth(dia, mes);
          const chave = format(dia, "yyyy-MM-dd");
          const doDia = porDia.get(chave) || [];

          return (
            <div
              key={chave}
              className={cn(
                "min-h-24 border-b border-r p-1.5 last:border-r-0",
                !doMes && "bg-muted/30"
              )}
            >
              <button
                type="button"
                onClick={() => onSelecionarDia?.(dia)}
                title="Agendar uma mensagem neste dia"
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full text-xs tabular-nums transition-colors hover:bg-accent",
                  doMes ? "text-foreground" : "text-muted-foreground",
                  isToday(dia) && "bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
                )}
              >
                {format(dia, "d")}
              </button>

              <div className="mt-1 space-y-0.5">
                {doDia.slice(0, 3).map((agendamento) => (
                  <button
                    key={agendamento.id}
                    type="button"
                    onClick={() => onAbrir?.(agendamento)}
                    title={agendamento.body}
                    className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[11px] leading-tight hover:bg-accent"
                  >
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        COR[agendamento.status] || "bg-muted-foreground"
                      )}
                    />
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {format(agendamento.quando, "HH:mm")}
                    </span>
                    <span className="truncate">
                      {agendamento.contact?.name || "Sem contato"}
                    </span>
                  </button>
                ))}

                {doDia.length > 3 && (
                  <p className="px-1 text-[11px] text-muted-foreground">
                    +{doDia.length - 3} neste dia
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 border-t px-4 py-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Vai ser enviada
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
          Já enviada
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
          Não será enviada
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
          Não conseguiu enviar
        </span>
      </div>
    </div>
  );
};

export default ScheduleCalendar;
